import { useCallback } from 'react'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import type { AudienceBreakdownData } from '../components/AudienceBreakdownSection'

interface UseAudienceCalculatorProps {
  selectedEntityId: string | undefined
  selectedEntityType: string | null
  activeCompetition: boolean
  visibleToRoles: string[]
  onLoadingChange?: (isLoading: boolean) => void
  onDataChange?: (data: AudienceBreakdownData | null) => void
  onError?: (error: string | null) => void
}

export const useAudienceCalculator = ({
  selectedEntityId,
  selectedEntityType,
  activeCompetition,
  visibleToRoles,
  onLoadingChange,
  onDataChange,
  onError,
}: UseAudienceCalculatorProps) => {
  const calculateAudience = useCallback(async () => {
    if (!selectedEntityId || !selectedEntityType) {
      onLoadingChange?.(false)
      onDataChange?.(null)
      return
    }

    try {
      onLoadingChange?.(true)
      onError?.(null)
      let type = ''
      if (selectedEntityType?.includes('org')) {
        type = 'organization'
      } else if (selectedEntityType?.includes('team')) {
        type = 'team'
      } else if (
        selectedEntityType?.includes('sched') ||
        selectedEntityType?.includes('competition')
      ) {
        type = 'competition'
      }

      const url = `/social-feed/${selectedEntityId}/${type}/audience_size`
      const body: Record<string, any> = {
        active_competition: activeCompetition,
      }
      if (visibleToRoles.length > 0) {
        body.role_types = visibleToRoles.map((role) => parseInt(role, 10))
      }
      const { data } = await req(url, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      onDataChange?.(data)
    } catch (error) {
      onDataChange?.(null)
      onError?.(getErrorMessage(error))
    } finally {
      onLoadingChange?.(false)
    }
  }, [
    selectedEntityId,
    selectedEntityType,
    activeCompetition,
    visibleToRoles,
    onLoadingChange,
    onDataChange,
    onError,
  ])

  return { calculateAudience }
}
